import { SocketClient } from "cbcore-ts"
import { NO, YES } from "uicore-ts"
import { RHFileUploadView } from "./RHFileUploadView"
import { RHFileItemView }   from "./RHFileItemView"
import { messageOfError } from "../../Core/RHErrorMessage"
import { RHCore } from "../../Core/RHCore"

export class RHOfferFileUploadView extends RHFileUploadView {

    static _uint8ArrayFromBase64String(base64: string): Uint8Array<ArrayBuffer> {
        const binary = atob(base64)
        const bytes = new Uint8Array(binary.length)
        for (let byteIndex = 0; byteIndex < binary.length; byteIndex++) {
            bytes[byteIndex] = binary.charCodeAt(byteIndex)
        }
        return bytes
    }

    static _base64StringFromBytes(bytes: Uint8Array): string {
        let binary = ""
        for (let byteIndex = 0; byteIndex < bytes.length; byteIndex++) {
            binary += String.fromCharCode(bytes[byteIndex])
        }
        return btoa(binary)
    }

    static _hexStringFromArrayBuffer(arrayBuffer: ArrayBuffer): string {
        return Array.from(new Uint8Array(arrayBuffer))
            .map(byte => byte.toString(16).padStart(2, "0"))
            .join("")
    }

    static async _encryptedFileDataFromPlaintextWithBase64Key(plaintext: ArrayBuffer, base64Key: string): Promise<{
        iv: string
        authTag: string
        ciphertext: string
    }> {
        const keyBytes = RHOfferFileUploadView._uint8ArrayFromBase64String(base64Key)
        const cryptoKey = await crypto.subtle.importKey(
            "raw",
            keyBytes,
            { name: "AES-GCM" },
            NO,
            ["encrypt"]
        )

        const iv = crypto.getRandomValues(new Uint8Array(12))

        const encrypted = await crypto.subtle.encrypt(
            { name: "AES-GCM", iv, tagLength: 128 },
            cryptoKey,
            plaintext
        )

        // Store the appended GCM authentication tag separately for the reveal service.
        const encryptedBytes = new Uint8Array(encrypted)
        const ciphertextBytes = encryptedBytes.slice(0, encryptedBytes.length - 16)
        const authTagBytes    = encryptedBytes.slice(encryptedBytes.length - 16)

        // The imported CryptoKey no longer needs the raw key bytes.
        keyBytes.fill(0)

        return {
            iv:         RHOfferFileUploadView._base64StringFromBytes(iv),
            authTag:    RHOfferFileUploadView._base64StringFromBytes(authTagBytes),
            ciphertext: RHOfferFileUploadView._base64StringFromBytes(ciphertextBytes)
        }
    }

    _procurementId: string = ""
    _offerId:       string = ""

    // The procurement must be known before uploading; offerId is optional until the draft is saved.
    configureForOffer(params: { procurementId: string; offerId?: string }): void {
        this._procurementId = params.procurementId
        this._offerId       = params.offerId ?? ""
    }

    override async uploadFile(file: File): Promise<boolean> {

        if (!this._procurementId) {
            this.showError("Hange ei ole määratud. Proovige uuesti.")
            return NO
        }

        const fileItemView = new RHFileItemView(
            undefined,
            (view: RHFileItemView) => this.fileItemDidDeleteFile(view)
        )
        fileItemView.configureAsUploading(file.name)
        this.fileItemViews.push(fileItemView)
        fileItemView.addedAsSubviewToView(this)
        this.setNeedsLayoutUpToRootView()

        try {

            fileItemView.updateProgress(5)

            const companyId = await RHCore.sharedInstance.companyAffiliationStore
                .activeCompanyIdAfterLoadingAffiliations()
            const mimeType = this._acceptedMimeTypeForFileNameAndReportedMimeType(file.name, file.type) ?? file.type
            const keyResponse = (await SocketClient.RequestOfferFileUploadKey({
                fileName:      file.name,
                mimeType,
                fileSize:      file.size,
                procurementId: this._procurementId,
                offerId:       this._offerId,
                companyId
            })).result

            if (!keyResponse?.fileId || !keyResponse?.presignedUrl) {
                throw new Error("Failed to get an upload URL from server")
            }

            const { fileId, isEncrypted, presignedUrl } = keyResponse

            fileItemView.updateProgress(15)

            let fileBlob:  Blob
            let confirmUploadedFile: () => Promise<boolean | undefined>

            if (isEncrypted) {
                if (!keyResponse.plaintextKey) {
                    throw new Error("Failed to get encryption key from server")
                }
                const plaintextKey = keyResponse.plaintextKey

                const plaintextBuffer = await file.arrayBuffer()

                const hashBuffer  = await crypto.subtle.digest("SHA-256", plaintextBuffer)
                const contentHash = RHOfferFileUploadView._hexStringFromArrayBuffer(hashBuffer)

                fileItemView.updateProgress(25)

                const { iv, authTag, ciphertext } = await RHOfferFileUploadView
                    ._encryptedFileDataFromPlaintextWithBase64Key(plaintextBuffer, plaintextKey)

                const ciphertextBytes = RHOfferFileUploadView._uint8ArrayFromBase64String(ciphertext)
                fileBlob = new Blob([ciphertextBytes], { type: "application/octet-stream" })

                // The server verifies uploaded bytes without decrypting their contents.
                const cipherHashBuffer = await crypto.subtle.digest("SHA-256", ciphertextBytes)
                const cipherHash       = RHOfferFileUploadView._hexStringFromArrayBuffer(cipherHashBuffer)

                confirmUploadedFile = async () => (await SocketClient.ConfirmEncryptedFileUpload({
                    fileId, iv, authTag, contentHash, cipherHash
                })).result?.success
            }
            else {
                // The server allows plaintext uploads only when encryption is not required.
                fileBlob = file
                confirmUploadedFile = async () => (await SocketClient.ConfirmFileUpload({ fileId })).result?.success
            }

            fileItemView.updateProgress(30)

            await this.uploadToStorage(
                fileBlob,
                { ...presignedUrl, fileId },
                (progress) => {
                    fileItemView.updateProgress(30 + Math.round(progress * 0.6))
                }
            )

            fileItemView.updateProgress(90)

            const didConfirm = await confirmUploadedFile()

            if (!didConfirm) {
                throw new Error("Upload confirmation failed")
            }

            fileItemView.updateProgress(100)

            this._uploadedFiles.push({
                id:   fileId,
                name: file.name,
                type: mimeType,
                size: file.size
            })

            fileItemView.configureWithFileIdAndName(fileId, file.name)
            this.setNeedsLayoutUpToRootView()
            return YES

        } catch (error) {
            console.error("[RHOfferFileUploadView] Upload error:", error)
            fileItemView.removeFromSuperview()
            this.fileItemViews = this.fileItemViews.filter(view => view !== fileItemView)
            this.showError(`Faili üleslaadimise viga: ${messageOfError(error) ?? ""}`)
            this.setNeedsLayoutUpToRootView()
            return NO
        }
    }

}
